import type {
  CompanyArticle,
  CompanyArticles,
  CompanyArticlesRequest,
  CompanyArticlesWithoutSanitized,
} from "@/modules/companies/client/api/types"
import type { DOMWindow } from "jsdom"

import { getResultFromResponse } from "@/modules/apiRequests/handlers"
import { handleFetchError } from "@/modules/fetch/errorHandler"
import { fetcher } from "@/modules/fetch/fetcher"
import { toCamelCaseKeys } from "@/modules/format/object/toCamelCaseKeys"
import { toSnakeCaseKeys } from "@/modules/format/object/toSnakeCaseKeys"

export const COMPANY_ARTICLES_QUERY_KEY = "companies::company::articles"
const COMPANY_ARTICLES_ENDPOINT = "/jobteaser.company.services.article.v1alpha1/ArticleService/GetArticles"

type GetCompanyArticlesParameters =
  | {
      windowInstance: DOMWindow
      params: CompanyArticlesRequest
      sanitize: (dirty: string, windowInstance: DOMWindow) => string
    }
  | {
      windowInstance?: undefined
      params: CompanyArticlesRequest
      sanitize: (dirty: string) => string
    }

export const getCompanyArticles = async ({
  params,
  sanitize,
  windowInstance,
}: GetCompanyArticlesParameters): Promise<CompanyArticles> => {
  const { nextRequest, ...restParams } = params

  return fetcher(COMPANY_ARTICLES_ENDPOINT, {
    nextRequest,
    requestInit: {
      body: JSON.stringify(toSnakeCaseKeys(restParams)),
      method: "POST",
    },
    serviceDomain: "APIGW_IN_KRAKEND_API",
  })
    .then(async response => {
      const enhancedResults = await getResultFromResponse<CompanyArticlesWithoutSanitized>({
        transformResult: toCamelCaseKeys<CompanyArticlesWithoutSanitized>,
      })(response)

      const enhancedArticles: CompanyArticles = {
        ...enhancedResults,
        articles: enhancedResults.articles.map((article): CompanyArticle => {
          const { content, ...rest } = article
          return {
            ...rest,
            contentSanitized: windowInstance ? sanitize(content, windowInstance) : sanitize(content),
          }
        }),
        nextPage:
          params.page_offset + params.page_limit > enhancedResults.filteredTotal
            ? null
            : params.page_offset + params.page_limit,
      }

      return enhancedArticles
    })
    .catch(handleFetchError(COMPANY_ARTICLES_ENDPOINT))
}
